import {
  RecordSelectionDialog,
  BaseRecordSelectionDialogProps,
  type FetchPageParams,
} from "@/components/RecordSelectionDialog/RecordSelectionDialog";
import { VideoScript, VideoScriptSearchQuery } from "@/services/bedrock";
import { URLS } from "@/utils/routes";
import { useVideoScriptSearch } from "@/utils/useVideoScriptSearch";
import { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { useSocialCoachSession } from "@/utils";

const VideoScriptSelectionDialog: React.FC<BaseRecordSelectionDialogProps<VideoScript>> = ({ ...props }) => {
  const { isCoach } = useSocialCoachSession();
  const { fetchVideoScriptsPage } = useVideoScriptSearch();

  const fetchPage = useCallback(
    async (params: FetchPageParams, signal: AbortSignal) => {
      const filters: VideoScriptSearchQuery = {
        searchTerm: params.search,
        page: params.page,
        perPage: params.perPage,
        sort: params.sort?.id as "created_date" | "updated_date" | undefined,
        sortDirection: params.sort ? (params.sort.desc ? "desc" : "asc") : undefined,
      };
      return fetchVideoScriptsPage(filters, signal);
    },
    [fetchVideoScriptsPage]
  );

  const columns: ColumnDef<VideoScript, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "title",
        id: "title",
        header: "Title",
        cell: ({ row }) => row.original.title,
      },
      {
        accessorKey: "categories",
        id: "categories",
        header: "Categories",
        cell: ({ row }) => row.original.categories.map(c => c.name).join(", "),
      },
      {
        accessorKey: "content",
        id: "content",
        header: "Content",
        cell: ({ row }) => row.original.content,
      },
    ],
    []
  );

  const videoScriptsUrl = isCoach ? URLS.coach.newVideoScript : URLS.player.newVideoScript;

  return (
    <RecordSelectionDialog
      {...props}
      fetchPage={fetchPage}
      columns={columns}
      secondaryAction={{
        label: "Create New Script",
        onClick: () => window.open(videoScriptsUrl, "_blank"),
      }}
    />
  );
};

export { VideoScriptSelectionDialog };
